import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { Http } from '../utils/http';
import Layout from './Layout';
import { RecordDisplay } from '../models/RecordDisplay';
import {iconForType, labelForMandateType, labelForType} from '../utils/typeResolver';
import { Relationship } from './Helpers';
import { AdvancedSearchQuery } from '../models/AdvancedSearch';
import { PageRoute } from '../models/PageRoute';


const SubjectPage: React.FC<PageRoute> = (route: PageRoute) => {
  const [currentSubject, setCurrentSubject] = useState<any | null>(null);
  const [notFoundRedirect, setNotFoundRedirect] = useState(false);
  const params = useParams();
  const qsaId: string = params.qsaId;

  if (!currentSubject) {
    Http.get()
      .fetchByQSAID(qsaId, 'subject')
      .then((json: any) => {
        if (json) {
          setCurrentSubject(new RecordDisplay(json));
        } else {
          setNotFoundRedirect(true);
        }
      })
      .catch((exception: Error) => {
        console.error(exception);
        setNotFoundRedirect(true);
      });
  }

  if (notFoundRedirect) {
    return <Navigate to="/404" replace={false} />;
  } else if (!currentSubject) {
    return <Layout skipFooter={true} />;
  } else {
    route.setPageTitle(`Subject: ${currentSubject.get('title')}`);

    const searchURLForTerms = (terms: any) => {
      let query = AdvancedSearchQuery.emptyQuery();

      terms.forEach((termData: any) => {
        const termType = termData['term_type'];
        let relatedQueryTargetField = 'non_atsi_subjects';
        if (termType === 'Aboriginal and/or Torres Strait Islander Subject') {
          relatedQueryTargetField = 'atsi_subjects';
        } else if (termType === 'Aboriginal and/or Torres Strait Islander Language/Group') {
          relatedQueryTargetField = 'atsi_language_group';
        } else if (termType === 'Place') {
          relatedQueryTargetField = 'place';
        }

        query = query.addStickyFilter(relatedQueryTargetField, termData['term'], termData['term']);
      });

      return `/search?${query.toQueryString()}`;
    };

    return (
      <Layout>
        <div className="row">
          <div className="col-sm-12">
            <h1>
              {currentSubject.get('title')}
              <div>
                <div className="badge">
                  <small>
                    <strong>
                      <i className={iconForType('subject')} aria-hidden="true" />
                      &nbsp;{labelForType('subject')}
                    </strong>
                  </small>
                </div>
              </div>
            </h1>

            <section className="core-information">
              <h2 className="sr-only">Basic information</h2>
              <p className="lead">{currentSubject.get('note')}</p>

              <ul className="list-group list-group-horizontal-md">
                <li className="list-group-item">
                  <span className="small">ID</span>
                  <br />
                  {currentSubject.get('qsa_id_prefixed')}
                </li>
                <li className="list-group-item">
                  <span className="small">Source</span>
                  <br />
                  {currentSubject.get('source')}
                </li>
                {currentSubject.getMaybe('authority_id', (value: any) => {
                  return (<li className="list-group-item">
                    <span className="small">Authority ID</span>
                    <br/>
                    {value}
                  </li>)
                })}
              </ul>

              <h3 className="sr-only">Subject descriptive metadata</h3>

              {currentSubject.getMaybe('terms', (terms: any[]) => {
                if (terms.length > 1) {
                  return (
                      <>
                        <h4 className="sr-only">Terms</h4>
                        <table className="table table-sm terms-table">
                          <thead>
                          <tr>
                            <th>Term</th>
                            <th>Term Type</th>
                            <th><span className="sr-only">Browse Related Records</span></th>
                          </tr>
                          </thead>
                          <tbody>
                          {
                            terms.map((termData:any) => {
                              return (<tr>
                                <td>{termData['term']}</td>
                                <td>{termData['term_type_label']}</td>
                                <td>
                                  <Link to={searchURLForTerms([termData])} className="qg-btn btn-primary btn-xs">
                                    Browse Related Records
                                  </Link>
                                </td>
                              </tr>)
                            })
                          }
                          </tbody>
                        </table>
                      </>
                  );
                } else {
                  return <></>;
                }
              })}

              <ul className="list-group list-group-flush">
                {currentSubject.getMaybe('terms', (terms: any) => {
                  if (terms.length === 1) {
                    return (
                        <li className="list-group-item list-group-item-action">
                          <div className="d-flex w-100 justify-content-between">
                            <h4 className="mb-1">Term Type</h4>
                          </div>
                          <p className="mb-1">{terms[0]['term_type_label']}</p>
                        </li>
                    );
                  } else {
                    return <></>;
                  }
                })}
                {currentSubject.getMaybe('scope_note', (value: any) => {
                  return (
                    <li className="list-group-item list-group-item-action">
                      <div className="d-flex w-100 justify-content-between">
                        <h4 className="mb-1">Scope Note</h4>
                      </div>
                      <p className="mb-1">{value}</p>
                    </li>
                  );
                })}
                {currentSubject.getMaybe('used_for_names', (value: any) => {
                  return (value && value.length > 0 &&
                      <li className="list-group-item list-group-item-action">
                        <div className="d-flex w-100 justify-content-between">
                          <h4 className="mb-1">Used For</h4>
                        </div>
                        <ul className="mb-1">
                          {
                            (value.map((name:string) => <li>{name}</li>))
                          }
                        </ul>
                      </li>
                  );
                })}
              </ul>
            </section>

            {currentSubject.getMaybe('terms', (terms: any[]) => {
              return (<section>
                <h2>Relationships</h2>
                {
                  <Link to={searchURLForTerms(terms)} className="qg-btn btn-primary btn-sm">
                    Browse Related Records
                  </Link>
                }
              </section>);
            })}
          </div>
        </div>
      </Layout>
    );
  }
};

export default SubjectPage;
